/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021-2025, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

/**
 * @module bulb-image-carousel/bulbimagecarouseltoolbar
 */

import {
  isWidget, Plugin, ViewDocumentSelection, WidgetToolbarRepository,
} from 'ckeditor5';

import { isObject } from 'lodash';
import { getToolBarConfig } from '../../editorcustomconfig.constant';

/**
 * The image carousel toolbar plugin. It creates a toolbar for the image carousel that shows up when the figure
 * is selected.
 *
 * Instances of toolbar components (e.g. buttons) are created based on the
 * {@link module:bulb-video/BulbImageCarouselToolbar#toolbar `bulbImageCarousel.toolbar` configuration option}.
 *
 * @extends Plugin
 */
export default class BulbImageCarouselToolbar extends Plugin {

  /**
   * @inheritDoc
   */
  public static get requires() {
    return [WidgetToolbarRepository] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbImageCarouselToolbar' as const;
  }

  /**
   * @inheritDoc
   */
  public afterInit(): void {
    const { editor } = this;
    const widgetToolbarRepository = editor.plugins.get(WidgetToolbarRepository);

    widgetToolbarRepository.register('imageCarousel', {
      items: this.normalizeDeclarativeConfig(getToolBarConfig('imageCarousel', CKEBulbData.readOnly)),
      getRelatedElement: this.getSelectedMediaViewWidget,
    });
  }

  /**
   * Convert the dropdown definitions to their keys registered in the ComponentFactory.
   * The registration precess should be handled by the plugin which handles the UI of a particular feature.
   */
  private normalizeDeclarativeConfig(config: any) {
    return config.map((item: { name: any; }) => (isObject(item) ? item.name : item));
  }

  /**
   * Get the selected media view widget.
   *
   * @param selection The current selection.
   * @private
   */
  private getSelectedMediaViewWidget(selection: ViewDocumentSelection) {
    const viewElement = selection.getSelectedElement();

    if (viewElement && !!viewElement.getCustomProperty('imageCarousel') && isWidget(viewElement)) {
      return viewElement;
    }

    return null;
  }
}
